/**
 * CrewBoard — the crew leaderboard in the standard CRM design (Ship 7 of the
 * My Business dashboard plan, Aug 24 2026). Replaces the `.crew-app`-skinned
 * Leaderboard.tsx for the Board tab — the DAILY surface (800 chip taps/60d,
 * spread evenly Mon–Fri) — while the low-traffic tabs (stats wall, audit,
 * shout-outs) keep the legacy skin until their fate is decided.
 *
 * Presentation only. Every number comes from the same modules as before:
 * crewQueries (crew_leaderboard view, feeds, goals, streaks) and
 * lib/weeklySpiffs (the SPIFF single source of truth shared with Chad's Ops
 * Review board). Do not re-inline rules here.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD, fmtCount } from "../../lib/analytics";
import {
  type CrewActivityEvent,
  type CrewLeaderboardRow,
  type SpiffEvent,
  type Timeframe,
  type WeeklyGoalsMap,
  type GoalMetricId,
  fetchCommitmentStreaks,
  fetchLeaderboard,
  fetchRecentActivity,
  fetchRecentSpiffs,
  fetchWeeklyGoalsMap,
  fetchWeeklySpiffData,
} from "./crewQueries";
import {
  FIVE_STAR_REVIEW_SPIFF,
  PERFECT_WEEK_TARGET,
  addDaysISO,
  SPIFF_PROGRAM,
  computeSpiffSummary,
  earnedFor,
  fmtReviewStars,
  mondayOf,
  windowClosed,
  type ReviewCreditRow,
  type SpiffActivityRow,
  type SpiffKey,
  type SpiffStat,
} from "../../lib/weeklySpiffs";
import { isCrewManager } from "./crewAuth";
import { fetchCalendarBuilds } from "../../lib/buildRevenue";
import GoalsAdmin from "./GoalsAdmin";

// Fallback weekly per-rep targets when no explicit goals exist (mirrors the
// legacy board's DEFAULT_GOALS).
const DEFAULT_PER_REP: Record<GoalMetricId, number> = {
  apps: 5,
  claims: 3,
  builds_scheduled: 1,
  builds: 1,
};

const GOAL_LABELS: Record<GoalMetricId, string> = {
  apps: "Apps",
  claims: "Claims",
  builds_scheduled: "Signed IA",
  builds: "Builds",
};

// ── small helpers (lifted from the legacy board — pure) ───────────────────
function initialsFrom(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
const AVATAR_BG = ["#2a2e30", "#345c72", "#c14a1b", "#1e5a7a", "#3a7ba6", "#2a7a43", "#5a4a7a", "#7a3a5a"];
function avatarColor(userId: string) {
  let h = 0;
  for (const c of userId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_BG[h % AVATAR_BG.length]!;
}
const firstName = (full: string) => full.trim().split(/\s+/)[0] ?? full;
function fmtRelative(iso: string) {
  const mins = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
function daysUntil(iso: string) {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 86400000));
}

interface Ranked {
  row: CrewLeaderboardRow;
  score: number;
  spiffCents: number;
  apps: number;
  claims: number;
  buildsScheduled: number;
  builds: number;
  rank: number;
  color: string;
  firstName: string;
  initials: string;
}

/** Same ranking model as the legacy board: points, spiff cents tiebreak. */
function buildRanking(rows: CrewLeaderboardRow[], tf: Timeframe): Ranked[] {
  const pick = (w: number, m: number, y: number) => (tf === "week" ? w : tf === "month" ? m : y);
  return rows
    .map((r) => ({
      row: r,
      score: Math.round(pick(r.points_week, r.points_month, r.points_ytd)),
      spiffCents: Math.round(pick(r.spiffs_week_cents, r.spiffs_month_cents, r.spiffs_ytd_cents)),
      apps: Number(pick(r.apps_week, r.apps_month, r.apps_ytd)),
      claims: Number(pick(r.claims_week, r.claims_month, r.claims_ytd)),
      buildsScheduled: Number(pick(r.builds_scheduled_week, r.builds_scheduled_month, r.builds_scheduled_ytd)),
      builds: Number(pick(r.builds_week, r.builds_month, r.builds_ytd)),
      rank: 0,
      color: avatarColor(r.user_id),
      firstName: firstName(r.full_name),
      initials: initialsFrom(r.full_name),
    }))
    // On-leave reps only rank when a split close (etc.) earned them points.
    .filter((r) => !r.row.on_leave || r.score > 0)
    .sort((a, b) => b.score - a.score || b.spiffCents - a.spiffCents)
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

/** Your SPIFF progress line per program entry (data from weeklySpiffs only). */
function progressLine(stat: SpiffStat | null, key: SpiffKey): string {
  if (!stat) return "—";
  switch (key) {
    case "monday": return `${fmtCount(stat.claimsMon)}/1 claim Mon`;
    case "tuesday": return `${fmtCount(stat.apptsTue)}/2 appts by Tue`;
    case "hump": return `${fmtCount(stat.claimsWed)}/3 claims by Wed`;
    case "perfect": return `${fmtCount(stat.perfectTotal)}/${PERFECT_WEEK_TARGET} claims + IAs`;
  }
}

const MEDALS = ["🥇", "🥈", "🥉"];

export default function CrewBoard() {
  const { member } = useAuth();
  const [tf, setTf] = useState<Timeframe>("week");
  const [rows, setRows] = useState<CrewLeaderboardRow[]>([]);
  const [activity, setActivity] = useState<CrewActivityEvent[]>([]);
  const [spiffEvents, setSpiffEvents] = useState<SpiffEvent[]>([]);
  const [goals, setGoals] = useState<WeeklyGoalsMap>({});
  // Builds on the calendar this week, per person (the Builds goal actual).
  // null = the read failed, so the row falls back to the crew credit.
  const [calBuilds, setCalBuilds] = useState<Record<string, number> | null>(null);
  const [streaks, setStreaks] = useState<Record<string, number>>({});
  const [spiffActivity, setSpiffActivity] = useState<SpiffActivityRow[] | null>(null);
  const [spiffMembers, setSpiffMembers] = useState<Array<{ id: string; full_name: string | null }>>([]);
  // 5-Star Review credits for the week; null = the read failed (tile says so).
  const [spiffReviews, setSpiffReviews] = useState<ReviewCreditRow[] | null>([]);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [expandedGoal, setExpandedGoal] = useState<GoalMetricId | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const isManager = isCrewManager(member);
  const weekMonday = useMemo(() => mondayOf(), []);

  async function reload() {
    try {
      setErr(null);
      const [lb, act, sp, gm, st, spiffData] = await Promise.all([
        fetchLeaderboard(),
        fetchRecentActivity(10),
        fetchRecentSpiffs(10),
        fetchWeeklyGoalsMap(),
        fetchCommitmentStreaks(),
        fetchWeeklySpiffData(weekMonday),
      ]);
      setRows(lb);
      setActivity(act);
      setSpiffEvents(sp);
      setGoals(gm);
      setStreaks(st);
      setSpiffActivity(spiffData.activity);
      setSpiffMembers(spiffData.members);
      setSpiffReviews(spiffData.reviews);
      // Oct 27, 2026 (Greg): the Builds goal counts builds scheduled FOR this
      // week (first build day Mon–Sun), not the crew credit, which lands a
      // Monday build in the week before. Best-effort: a failure keeps the credit.
      fetchCalendarBuilds(weekMonday, addDaysISO(weekMonday, 7)).then(setCalBuilds, (e) => {
        console.warn("crew_calendar_builds failed", e);
        setCalBuilds(null);
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load the board");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRealtimeTable("spiffs", reload);
  useRealtimeTable("crew_activity_log", reload);
  useRealtimeTable("shoutouts", reload);

  const ranked = useMemo(() => buildRanking(rows, tf), [rows, tf]);
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  // Battle: first adjacent pair within 5 pts (week view only) — same rule.
  const battle = useMemo(() => {
    if (tf !== "week" || ranked.length < 2) return null;
    for (let i = 1; i < ranked.length; i++) {
      const gap = ranked[i - 1]!.score - ranked[i]!.score;
      if (gap <= 5) return { ahead: ranked[i - 1]!, behind: ranked[i]!, gap, rankAt: i };
    }
    return null;
  }, [ranked, tf]);

  const spiffSummary = useMemo(
    () =>
      spiffActivity && spiffMembers.length
        ? computeSpiffSummary(spiffActivity, spiffMembers, weekMonday, spiffReviews)
        : null,
    [spiffActivity, spiffMembers, weekMonday, spiffReviews],
  );
  const youStat = spiffSummary && member ? spiffSummary.perRM[member.id] ?? null : null;

  // Goals: company goal ?? Σ personal ?? default × crew size (legacy rule).
  const goalRows = useMemo(() => {
    const builds = (r: (typeof ranked)[number]) => (calBuilds ? calBuilds[r.row.user_id] ?? 0 : r.builds);
    return (Object.keys(GOAL_LABELS) as GoalMetricId[]).map((metric) => {
      const actual = ranked.reduce((s, r) => s + (metric === "apps" ? r.apps : metric === "claims" ? r.claims : metric === "builds_scheduled" ? r.buildsScheduled : builds(r)), 0);
      const sumPersonal = Object.entries(goals)
        .filter(([k]) => k !== "company")
        .reduce((s, [, g]) => s + (g[metric] ?? 0), 0);
      const goal = goals["company"]?.[metric] ?? (sumPersonal > 0 ? sumPersonal : DEFAULT_PER_REP[metric] * Math.max(ranked.filter((r) => !r.row.on_leave).length, 1));
      const people = ranked
        .filter((r) => !r.row.on_leave)
        .map((r) => ({
          name: r.firstName,
          id: r.row.user_id,
          actual: metric === "apps" ? r.apps : metric === "claims" ? r.claims : metric === "builds_scheduled" ? r.buildsScheduled : builds(r),
          goal: goals[r.row.user_id]?.[metric] ?? null,
        }))
        .sort((a, b) => {
          const ah = a.goal != null && a.goal > 0 && a.actual >= a.goal ? 1 : 0;
          const bh = b.goal != null && b.goal > 0 && b.actual >= b.goal ? 1 : 0;
          if (ah !== bh) return bh - ah;
          const ap = a.goal ? a.actual / a.goal : 0, bp = b.goal ? b.actual / b.goal : 0;
          return bp - ap || b.actual - a.actual;
        });
      return { metric, actual, goal, people };
    });
  }, [ranked, goals, calBuilds]);

  // Live feed: merge activity + spiffs, newest first (legacy rule).
  const feed = useMemo(() => {
    const merged = [
      ...activity.map((e) => ({ t: new Date(e.occurred_at).getTime(), node: feedLabel(e), when: e.occurred_at })),
      ...spiffEvents.map((e) => ({ t: new Date(e.claimed_at).getTime(), node: `💰 ${firstName(e.user_name)} claimed ${e.name} · ${fmtUSD(e.amount_cents / 100)}`, when: e.claimed_at })),
    ];
    return merged.sort((a, b) => b.t - a.t).slice(0, 10);
  }, [activity, spiffEvents]);

  const yearEnd = rows[0]?.year_end ?? null;

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading the board…</div>;
  if (err) return <div className="m-4 p-4 bg-red-50 text-red-700 rounded-xl text-sm">Couldn't load the board — {err}</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4">
      {/* Timeframe toggle + goals editor */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-bold">
          {(["week", "month", "ytd"] as Timeframe[]).map((k, i) => (
            <button key={k} onClick={() => setTf(k)}
              className={"px-4 py-1.5 " + (i > 0 ? "border-l border-slate-200 " : "") + (tf === k ? "bg-bardin-ink text-white" : "bg-white text-slate-500 hover:bg-slate-50")}>
              {k === "week" ? "Week" : k === "month" ? "Month" : "YTD"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {tf === "ytd" && yearEnd && (
            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              🎄 {daysUntil(yearEnd)} days to the crowning
            </span>
          )}
          {isManager && (
            <button onClick={() => setGoalsOpen(true)}
              className="text-[11px] font-bold text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
              ✏️ Set goals
            </button>
          )}
        </div>
      </div>

      {/* Weekly SPIFF strip */}
      {tf === "week" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">This week's SPIFFs</span>
            {/* The rep's own total — threshold SPIFFs plus their own 5★ review SPIFF. */}
            {youStat && youStat.totalEarned + youStat.reviewEarned > 0 && (
              <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">You've earned {fmtUSD(youStat.totalEarned + youStat.reviewEarned)}</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {SPIFF_PROGRAM.map((p) => {
              const earned = youStat ? earnedFor(youStat, p.key) : false;
              const closed = windowClosed(p.key);
              const teamCount = spiffSummary?.counts[p.key] ?? 0;
              return (
                <div key={p.key} title={p.shortRule}
                  className={"rounded-xl border p-2.5 " + (earned ? "border-emerald-300 bg-emerald-50/60" : closed ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200")}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">{p.name}</span>
                    {earned ? <span className="text-emerald-600 text-sm">✓</span> : closed ? <span className="text-[9px] font-bold text-slate-400">CLOSED</span> : null}
                  </div>
                  <div className="text-base font-extrabold text-bardin-ink">{fmtUSD(p.prizeCents / 100)}</div>
                  <div className={"text-[10px] mt-0.5 " + (earned ? "text-emerald-700 font-bold" : "text-slate-500")}>{progressLine(youStat, p.key)}</div>
                  {teamCount > 0 && <div className="text-[9px] text-slate-400 mt-0.5">{teamCount} on the team earned it</div>}
                </div>
              );
            })}
            {/* 5-Star Review SPIFF (Oct 19, 2026) — per review, Monday noon to Monday noon, never closes.
                It pays the review's commission point; the dollars here are the viewer's own. */}
            {(() => {
              const mine = youStat?.reviews ?? 0;
              const earned = mine > 0;
              const team = spiffSummary?.reviewReps ?? 0;
              return (
                <div title={FIVE_STAR_REVIEW_SPIFF.rule}
                  className={"col-span-2 sm:col-span-1 rounded-xl border p-2.5 " + (earned ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200")}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">⭐ {FIVE_STAR_REVIEW_SPIFF.name}</span>
                    {earned ? <span className="text-emerald-600 text-sm">✓</span> : null}
                  </div>
                  <div className="text-base font-extrabold text-bardin-ink">5★ point</div>
                  <div className={"text-[10px] mt-0.5 " + (earned ? "text-emerald-700 font-bold" : "text-slate-500")}>
                    {earned && youStat
                      ? youStat.reviewEarned > 0
                        ? `${fmtReviewStars(mine)} this week · ${fmtUSD(youStat.reviewEarned)}`
                        : youStat.reviewAmountKnown
                          ? `${fmtReviewStars(mine)} this week · recognized, no SPIFF on these jobs`
                          : `${fmtReviewStars(mine)} this week`
                      : FIVE_STAR_REVIEW_SPIFF.shortRule}
                  </div>
                  {spiffSummary && !spiffSummary.reviewsKnown ? (
                    <div className="text-[9px] text-amber-600 mt-0.5">couldn't load reviews</div>
                  ) : team > 0 ? (
                    <div className="text-[9px] text-slate-400 mt-0.5">{team} on the team got a 5★ review</div>
                  ) : null}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Team goals (week) */}
      {tf === "week" && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-3 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">Team targets · this week<span className="normal-case tracking-normal font-normal text-slate-400"> · builds = on the calendar this week</span></div>
          {goalRows.map(({ metric, actual, goal, people }) => {
            const pct = goal > 0 ? Math.min(100, (actual / goal) * 100) : 0;
            const hit = goal > 0 && actual >= goal;
            const open = expandedGoal === metric;
            return (
              <div key={metric} className="border-b border-slate-50 last:border-b-0">
                <button onClick={() => setExpandedGoal(open ? null : metric)} className="w-full px-4 py-2.5 text-left hover:bg-slate-50/60">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold text-bardin-ink">{GOAL_LABELS[metric]}</span>
                    <span className={"font-bold " + (hit ? "text-emerald-600" : "text-slate-600")}>
                      {fmtCount(actual)}<span className="text-slate-400 font-normal">/{fmtCount(goal)}</span>
                      <span className="ml-1.5 text-slate-300">{open ? "▾" : "▸"}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={"h-full rounded-full " + (hit ? "bg-emerald-500" : "bg-bardin-teal")} style={{ width: `${pct}%` }} />
                  </div>
                </button>
                {open && (
                  <ul className="px-4 pb-2.5">
                    {people.map((p) => {
                      const phit = p.goal != null && p.goal > 0 && p.actual >= p.goal;
                      return (
                        <li key={p.id} className="flex items-center justify-between py-1 text-xs">
                          <span className="text-slate-600">{p.name}</span>
                          <span className={phit ? "font-bold text-emerald-600" : "text-slate-500"}>
                            {fmtCount(p.actual)}{p.goal != null && p.goal > 0 ? `/${fmtCount(p.goal)}` : ""}{phit ? " ✓" : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Battle */}
      {battle && (
        <div className="bg-white rounded-2xl border-2 border-bardin-orange/40 p-4 mb-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-bardin-orange mb-2">
            ⚡ Battle {battle.rankAt === 1 ? "for #1" : `for #${battle.rankAt}`} · {battle.gap === 0 ? "dead even" : `${battle.gap} pt${battle.gap === 1 ? "" : "s"} apart`}
          </div>
          <div className="flex items-center justify-between gap-3">
            {[{ r: battle.ahead, tag: "Leader" }, { r: battle.behind, tag: "Challenger" }].map(({ r, tag }, i) => (
              <div key={r.row.user_id} className={"flex items-center gap-2.5 " + (i === 1 ? "flex-row-reverse text-right" : "")}>
                <span className="w-10 h-10 rounded-full grid place-items-center text-white text-sm font-extrabold shrink-0" style={{ background: r.color }}>{r.initials}</span>
                <div>
                  <div className="text-sm font-bold text-bardin-ink">{r.firstName}</div>
                  <div className="text-[10px] text-slate-500">{tag} · {r.score} pts</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Podium */}
      {top3.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {top3.map((r, i) => (
            <div key={r.row.user_id} className={"bg-white rounded-2xl border p-3 text-center " + (i === 0 ? "border-amber-300 shadow-sm" : "border-slate-200")}>
              <div className="text-lg">{MEDALS[i]}</div>
              <span className="inline-grid w-11 h-11 rounded-full place-items-center text-white text-sm font-extrabold my-1" style={{ background: r.color }}>{r.initials}</span>
              <div className="text-sm font-bold text-bardin-ink truncate">{r.firstName}</div>
              {r.row.on_leave && <div className="text-[9px] font-bold uppercase text-slate-400">on leave</div>}
              <div className="text-lg font-extrabold text-bardin-ink leading-tight">{r.score}<span className="text-[10px] font-bold text-slate-400"> pts</span></div>
              <div className="text-[10px] text-slate-500 mt-0.5">{fmtCount(r.apps)}a · {fmtCount(r.claims)}c · {fmtCount(r.buildsScheduled)}s · {fmtCount(r.builds)}b</div>
              {r.spiffCents > 0 && <div className="text-[10px] font-bold text-emerald-600 mt-0.5">{fmtUSD(r.spiffCents / 100)} SPIFF</div>}
              {(streaks[r.row.user_id] ?? 0) > 0 && (
                <span className="inline-block mt-1 text-[9px] font-extrabold uppercase rounded px-1.5 py-0.5 text-white" style={{ background: "linear-gradient(90deg,#f46530,#ff9e7a)" }}>🔥 {streaks[r.row.user_id]}-wk</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Ranked list 4..N */}
      {rest.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-3 overflow-hidden">
          <ul className="divide-y divide-slate-50">
            {rest.map((r) => (
              <li key={r.row.user_id} className={"px-4 py-2.5 flex items-center gap-3 " + (member && r.row.user_id === member.id ? "bg-bardin-teal/5" : "")}>
                <span className="text-xs font-extrabold text-slate-400 w-6">#{r.rank}</span>
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-extrabold shrink-0" style={{ background: r.color }}>{r.initials}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-bardin-ink truncate">
                    {r.row.full_name}
                    {(streaks[r.row.user_id] ?? 0) > 0 && <span className="ml-1.5 text-[9px] font-extrabold text-bardin-orange">🔥{streaks[r.row.user_id]}</span>}
                    {r.row.on_leave && <span className="ml-1.5 text-[9px] font-bold uppercase text-slate-400">on leave</span>}
                  </div>
                  <div className="text-[10px] text-slate-400">{fmtCount(r.apps)}a · {fmtCount(r.claims)}c · {fmtCount(r.buildsScheduled)}s · {fmtCount(r.builds)}b{r.spiffCents > 0 ? ` · ${fmtUSD(r.spiffCents / 100)}` : ""}</div>
                </div>
                <span className="text-sm font-extrabold text-bardin-ink shrink-0">{r.score} pts</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Live feed */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">Just happened</div>
        {feed.length === 0 ? (
          <div className="px-4 py-5 text-center text-sm text-slate-400">Quiet out there — go make something happen.</div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {feed.map((f, i) => (
              <li key={i} className="px-4 py-2 flex items-center justify-between gap-2 text-sm">
                <span className="text-bardin-ink truncate">{f.node}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{fmtRelative(f.when)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Goals editor — legacy component; needs the crew token scope to paint. */}
      {goalsOpen && (
        <div className="crew-app">
          <GoalsAdmin onClose={() => { setGoalsOpen(false); reload(); }} />
        </div>
      )}
    </div>
  );
}

/** Feed line for an activity event (same vocabulary as the legacy board). */
function feedLabel(e: CrewActivityEvent): string {
  const who = firstName(e.user_name);
  switch (e.kind) {
    case "app": return `📅 ${who} set an appointment (+1)`;
    case "claim": return `📋 ${who} got a claim approved to file (+3)`;
    case "build_scheduled": return `📝 ${who} got an IA signed`;
    case "build": return `🗓 ${who} put a build on the calendar (+10)`;
    case "revenue": return `💵 ${who} logged revenue`;
    case "review": return `⭐ ${who} landed a review`;
    default: return `${who} did something`;
  }
}
